import { useTranslation } from 'react-i18next';

export interface UploadProgressModalProps {
  isUploading: boolean;
  uploadProgress: number;
  // 压缩进度（可选）
  isCompressing?: boolean;
  compressionProgress?: number;
  compressionDetail?: string;
}

export function UploadProgressModal({
  isUploading,
  uploadProgress,
  isCompressing = false,
  compressionProgress = 0,
  compressionDetail = '',
}: UploadProgressModalProps) {
  const { t } = useTranslation();

  // 压缩阶段或上传阶段都显示弹窗
  if (!isUploading && !isCompressing) {
    return null;
  }

  // 当前阶段的标题和进度
  const isInCompressionPhase = isCompressing;
  const currentProgress = isInCompressionPhase
    ? compressionProgress
    : uploadProgress;
  const title = isInCompressionPhase
    ? t('project.compressingFolders', {
        defaultValue: 'Compressing folders...',
      })
    : t('project.uploadingFiles');

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-white rounded-lg shadow-2xl p-6 sm:p-8 w-full max-w-md mx-4">
        <div className="flex items-center justify-between mb-2">
          <span className="text-lg font-semibold text-gray-800">{title}</span>
          <span className="text-2xl font-bold text-blue-600">
            {currentProgress}%
          </span>
        </div>
        {/* 压缩阶段显示当前处理的文件详情 */}
        {isInCompressionPhase && compressionDetail && (
          <p
            className="text-xs text-gray-500 mb-3 truncate"
            title={compressionDetail}
          >
            {compressionDetail}
          </p>
        )}
        <div className="w-full h-3 bg-gray-200 rounded-full overflow-hidden">
          <div
            className={`h-full transition-all duration-300 ease-out ${
              isInCompressionPhase ? 'bg-amber-500' : 'bg-blue-600'
            }`}
            style={{ width: `${currentProgress}%` }}
          />
        </div>
        {/* 阶段指示器 */}
        {isInCompressionPhase && (
          <div className="flex items-center gap-2 mt-3 text-xs text-gray-500">
            <div className="flex items-center gap-1.5">
              <div className="w-2 h-2 rounded-full bg-amber-500 animate-pulse" />
              <span>
                {t('project.stepCompressing', { defaultValue: 'Compressing' })}
              </span>
            </div>
            <span className="text-gray-300">→</span>
            <div className="flex items-center gap-1.5">
              <div className="w-2 h-2 rounded-full bg-gray-300" />
              <span>
                {t('project.stepUploading', { defaultValue: 'Uploading' })}
              </span>
            </div>
          </div>
        )}
        {isUploading && !isInCompressionPhase && (
          <div className="flex items-center gap-2 mt-3 text-xs text-gray-500">
            <div className="flex items-center gap-1.5">
              <div className="w-2 h-2 rounded-full bg-green-500" />
              <span>
                {t('project.stepCompressing', { defaultValue: 'Compressing' })}
              </span>
            </div>
            <span className="text-gray-300">→</span>
            <div className="flex items-center gap-1.5">
              <div className="w-2 h-2 rounded-full bg-blue-600 animate-pulse" />
              <span>
                {t('project.stepUploading', { defaultValue: 'Uploading' })}
              </span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default UploadProgressModal;
